import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaComments, FaTimes } from 'react-icons/fa';

import './TidioChat.less';

const TIDIO_SCRIPT_ID = 'tidio-chat-script';
const TIDIO_SCRIPT_URL = 'https://code.tidio.co/jyglfa2lfqap4kpuxscqqkxbldkgafsu.js';
const TIDIO_CHAT_EVENT = 'farmx_chat_with_us_clicked';
const DRAG_THRESHOLD = 4;
const VIEWPORT_MARGIN = 12;
const CLOSE_FALLBACK_DELAY = 100;
const CLOSE_RESTORE_DELAY = 16;

const clamp = (value, minimum, maximum) => (
  Math.min(Math.max(value, minimum), maximum)
);

export function openTidioChat() {
  const openChat = () => {
    if (!window.tidioChatApi) return;

    window.tidioChatApi.show();
    window.tidioChatApi.open();
    window.tidioChatApi.track(TIDIO_CHAT_EVENT);
  };

  if (window.tidioChatApi) openChat();
  else document.addEventListener('tidioChat-ready', openChat, { once: true });
}

export default function TidioChat({ showLauncher }) {
  const [isReady, setIsReady] = useState(Boolean(window.tidioChatApi));
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [launcherPosition, setLauncherPosition] = useState(null);
  const launcherRef = useRef(null);
  const dragState = useRef(null);
  const didDrag = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let restoreLauncherTimer;

    const handleOpen = () => {
      if (isMounted) setIsOpen(true);
    };

    const scheduleLauncherRestore = (delay) => {
      if (!isMounted) return;

      window.tidioChatApi?.hide();
      window.clearTimeout(restoreLauncherTimer);
      restoreLauncherTimer = window.setTimeout(() => {
        if (isMounted) setIsOpen(false);
      }, delay);
    };

    const handleBeforeClose = () => {
      // Hide before Tidio paints its own minimized launcher.
      scheduleLauncherRestore(CLOSE_FALLBACK_DELAY);
    };

    const handleClose = () => {
      scheduleLauncherRestore(CLOSE_RESTORE_DELAY);
    };

    const configureChat = () => {
      if (!isMounted || !window.tidioChatApi) return;

      window.tidioChatApi.hide();
      window.tidioChatApi.on('open', handleOpen);
      window.tidioChatApi.on('beforeClose', handleBeforeClose);
      window.tidioChatApi.on('close', handleClose);
      setIsReady(true);
    };

    if (window.tidioChatApi) configureChat();
    else {
      document.addEventListener('tidioChat-ready', configureChat);

      if (!document.getElementById(TIDIO_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = TIDIO_SCRIPT_ID;
        script.src = TIDIO_SCRIPT_URL;
        script.async = true;
        document.body.appendChild(script);
      }
    }

    return () => {
      isMounted = false;
      window.clearTimeout(restoreLauncherTimer);
      document.removeEventListener('tidioChat-ready', configureChat);

      if (window.tidioChatApi) {
        window.tidioChatApi.hide();
      } else {
        document.addEventListener('tidioChat-ready', () => {
          if (window.tidioChatApi) window.tidioChatApi.hide();
        }, { once: true });
      }
    };
  }, []);

  const openChat = () => {
    if (didDrag.current) {
      didDrag.current = false;
      return;
    }

    if (!window.tidioChatApi) return;

    setIsOpen(true);
    openTidioChat();
  };

  const startDragging = (event) => {
    if (event.button !== 0 || !launcherRef.current) return;

    const bounds = launcherRef.current.getBoundingClientRect();
    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
      width: bounds.width,
      height: bounds.height,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveLauncher = (event) => {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const distance = Math.hypot(
      event.clientX - drag.startX,
      event.clientY - drag.startY,
    );
    if (!drag.moved && distance < DRAG_THRESHOLD) return;

    drag.moved = true;
    didDrag.current = true;
    setLauncherPosition({
      left: clamp(
        event.clientX - drag.offsetX,
        VIEWPORT_MARGIN,
        Math.max(VIEWPORT_MARGIN, window.innerWidth - drag.width - VIEWPORT_MARGIN),
      ),
      top: clamp(
        event.clientY - drag.offsetY,
        VIEWPORT_MARGIN,
        Math.max(VIEWPORT_MARGIN, window.innerHeight - drag.height - VIEWPORT_MARGIN),
      ),
    });
  };

  const stopDragging = (event) => {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragState.current = null;
  };

  const dismissLauncher = () => {
    window.tidioChatApi?.hide();
    setIsDismissed(true);
  };

  if (isOpen || isDismissed || !showLauncher) return null;

  const positionStyle = launcherPosition
    ? {
      left: `${launcherPosition.left}px`,
      top: `${launcherPosition.top}px`,
      right: 'auto',
      bottom: 'auto',
    }
    : undefined;

  return (
    <div
      ref={launcherRef}
      className="tidio-chat-control"
      style={positionStyle}
    >
      <button
        type="button"
        className="tidio-chat-launcher"
        onClick={openChat}
        onPointerDown={startDragging}
        onPointerMove={moveLauncher}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        disabled={!isReady}
        aria-label="Chat with us. Drag to move."
      >
        <FaComments aria-hidden="true" />
        <span>Chat with us</span>
      </button>
      <button
        type="button"
        className="tidio-chat-dismiss"
        onClick={dismissLauncher}
        aria-label="Hide chat button until page refresh"
        title="Hide until refresh"
      >
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

TidioChat.propTypes = {
  showLauncher: PropTypes.bool,
};

TidioChat.defaultProps = {
  showLauncher: true,
};
